/**
 * Report zoom controller per M3X.REPORTS §0.4.
 *
 *   - Toolbar slider (50% - 200%, default 100%, step 10%)
 *   - Keyboard shortcuts ⌘+ / ⌘- / ⌘0 (Ctrl on Windows/Linux)
 *   - CSS transform: scale(N) on caller's content wrapper only
 *   - Preserve scroll position when zooming
 *   - Per-user persisted preference in localStorage keyed by report
 *     id + user id (`solvait.reports.zoom.{userId}.{reportId}`)
 *   - Touch gesture support (pinch zoom) on tablets via the
 *     dispatched `wheel` events with ctrlKey:true that browsers
 *     emit when the user pinches — handled in the same handler
 *
 * Exports never inherit zoom — the export always renders at 100%.
 */

import { useCallback, useEffect, useState } from "react";

export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2.0;
export const ZOOM_STEP = 0.1;
export const ZOOM_DEFAULT = 1.0;

/** Build the localStorage key for a (user, report) pair. */
export function zoomStorageKey(userId: string, reportId: string): string {
  return `solvait.reports.zoom.${userId}.${reportId}`;
}

/** Clamp a zoom value to the legal range. */
export function clampZoom(z: number): number {
  if (!Number.isFinite(z)) return ZOOM_DEFAULT;
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(z * 100) / 100));
}

/**
 * React hook for the report-zoom controller.
 *
 * Returns the current zoom, a setter, and an onWheel handler the
 * caller can pass to their report-content wrapper for pinch-zoom
 * support on tablets. Keyboard shortcuts are auto-bound.
 */
export function useReportZoom(opts: {
  userId: string | null;
  reportId: string;
  initial?: number;
  enableKeyboard?: boolean;
}): {
  zoom: number;
  setZoom: (z: number) => void;
  reset: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  onWheel: (e: WheelEvent | React.WheelEvent) => void;
} {
  const enableKeyboard = opts.enableKeyboard ?? true;
  const storageKey =
    opts.userId !== null ? zoomStorageKey(opts.userId, opts.reportId) : null;

  const [zoom, setZoomState] = useState<number>(() => {
    if (!storageKey) return opts.initial ?? ZOOM_DEFAULT;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return opts.initial ?? ZOOM_DEFAULT;
      return clampZoom(Number.parseFloat(raw));
    } catch {
      return opts.initial ?? ZOOM_DEFAULT;
    }
  });

  const setZoom = useCallback(
    (z: number) => {
      const next = clampZoom(z);
      setZoomState(next);
      if (storageKey) {
        try {
          window.localStorage.setItem(storageKey, String(next));
        } catch {
          // localStorage unavailable / quota — skip silently
        }
      }
    },
    [storageKey],
  );

  const reset = useCallback(() => setZoom(ZOOM_DEFAULT), [setZoom]);
  const zoomIn = useCallback(() => setZoom(zoom + ZOOM_STEP), [setZoom, zoom]);
  const zoomOut = useCallback(() => setZoom(zoom - ZOOM_STEP), [setZoom, zoom]);

  const onWheel = useCallback(
    (e: WheelEvent | React.WheelEvent) => {
      // Pinch on tablets => wheel with ctrlKey:true
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(zoom + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
    },
    [setZoom, zoom],
  );

  useEffect(() => {
    if (!enableKeyboard) return undefined;
    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setZoom(zoom + ZOOM_STEP);
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        setZoom(zoom - ZOOM_STEP);
      } else if (e.key === "0") {
        e.preventDefault();
        setZoom(ZOOM_DEFAULT);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enableKeyboard, setZoom, zoom]);

  return { zoom, setZoom, reset, zoomIn, zoomOut, onWheel };
}
